/**
 * FDR Entity Detail — Full entity view with inline editing and tabbed sections.
 * Props: { entityId, onBack, onOpenEntity }
 */
import React, { useState, useEffect, useCallback } from 'react';
import CopilotPanel from './CopilotPanel';
import { StatusPill, ContractWorkspace, AddFunctionModal } from './ContractManager';
import Attachments from './Attachments';
import AiSystemsTab from './AiSystemsTab';
import SecurityTab from './SecurityTab';

const API = process.env.REACT_APP_API_URL;

// ─── Badge config helpers ────────────────────────────────────────────────────

const ENTITY_TYPE_CFG = {
  first_tier:   { label: 'First-Tier',   color: 'bg-orange-50 text-orange-700 border-orange-200' },
  downstream:   { label: 'Downstream',   color: 'bg-orange-50 text-orange-700 border-orange-200' },
  related:      { label: 'Related',      color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const STATUS_CFG = {
  active:      { label: 'Active',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  inactive:    { label: 'Inactive',    color: 'bg-slate-100 text-slate-500 border-slate-200' },
  suspended:   { label: 'Suspended',   color: 'bg-red-50 text-red-700 border-red-200' },
  terminated:  { label: 'Terminated',  color: 'bg-slate-100 text-slate-400 border-slate-200' },
  pending:     { label: 'Pending',     color: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const RISK_LEVEL_CFG = {
  low:     { label: 'Low',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  medium:  { label: 'Medium',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  high:    { label: 'High',    color: 'bg-orange-50 text-orange-700 border-orange-200' },
  critical:{ label: 'Critical',color: 'bg-red-50 text-red-700 border-red-200' },
};

const SEVERITY_CFG = {
  low:      { label: 'Low',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  medium:   { label: 'Medium',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  high:     { label: 'High',     color: 'bg-orange-50 text-orange-700 border-orange-200' },
  critical: { label: 'Critical', color: 'bg-red-50 text-red-700 border-red-200' },
};

const FINDING_STATUS_CFG = {
  open:        { label: 'Open',        color: 'bg-red-50 text-red-700 border-red-200' },
  in_progress: { label: 'In Progress', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:    { label: 'Resolved',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed:      { label: 'Closed',      color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const ATTESTATION_STATUS_CFG = {
  pending:   { label: 'Pending',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  sent:      { label: 'Sent',      color: 'bg-sky-50 text-sky-700 border-sky-200' },
  received:  { label: 'Received',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  overdue:   { label: 'Overdue',   color: 'bg-red-50 text-red-700 border-red-200' },
  waived:    { label: 'Waived',    color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const CONFIDENCE_CFG = {
  high:   { label: 'High',   color: 'bg-red-50 text-red-700 border-red-200' },
  medium: { label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  low:    { label: 'Low',    color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const RESOLUTION_CFG = {
  confirmed_match:  { label: 'Confirmed Match',  color: 'bg-red-50 text-red-700 border-red-200' },
  false_positive:   { label: 'False Positive',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  under_review:     { label: 'Under Review',     color: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending:          { label: 'Pending',          color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const ASSESSMENT_STATUS_CFG = {
  scheduled:   { label: 'Scheduled',   color: 'bg-sky-50 text-sky-700 border-sky-200' },
  in_progress: { label: 'In Progress', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  completed:   { label: 'Completed',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled:   { label: 'Cancelled',   color: 'bg-slate-100 text-slate-400 border-slate-200' },
};

const CONTRACT_STATUS_CFG = {
  active:    { label: 'Active',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired:   { label: 'Expired',   color: 'bg-red-50 text-red-700 border-red-200' },
  pending:   { label: 'Pending',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  terminated:{ label: 'Terminated',color: 'bg-slate-100 text-slate-400 border-slate-200' },
  draft:     { label: 'Draft',     color: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const SCREENING_CONTACT_STATUS_CFG = {
  clear:         { label: 'Clear',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  flagged:       { label: 'Flagged',       color: 'bg-red-50 text-red-700 border-red-200' },
  pending:       { label: 'Pending',       color: 'bg-amber-50 text-amber-700 border-amber-200' },
  not_screened:  { label: 'Not Screened',  color: 'bg-slate-100 text-slate-400 border-slate-200' },
};

// ─── Shared micro-components ─────────────────────────────────────────────────

function Badge({ cfg, value, fallback }) {
  const entry = cfg?.[value?.toLowerCase?.()] ?? cfg?.[value];
  if (!entry) return <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-slate-50 text-slate-500 border-slate-200">{fallback ?? value ?? '—'}</span>;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${entry.color}`}>{entry.label}</span>;
}

function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200/60 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function CardSection({ title, children, action }) {
  return (
    <Card className="p-6">
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </Card>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">{label}</span>
      <span className="text-sm text-slate-700 font-medium">{value || <span className="text-slate-300">—</span>}</span>
    </div>
  );
}

function StatTile({ label, value, accent = false }) {
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-1 ${accent ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200/60'}`}>
      <span className={`text-2xl font-bold ${accent ? 'text-orange-700' : 'text-slate-800'}`}>{value ?? 0}</span>
      <span className="text-xs text-slate-500 font-medium">{label}</span>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="py-10 flex flex-col items-center gap-2">
      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4" />
        </svg>
      </div>
      <p className="text-sm text-slate-400">{message}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div className="p-12 flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-orange-500 animate-spin" />
    </div>
  );
}

function LabeledInput({ label, type = 'text', value, onChange, required }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}{required && ' *'}</label>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        required={required}
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
      />
    </div>
  );
}

function LabeledCheckbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-600">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={e => onChange(e.target.checked)}
        className="rounded border-slate-300 text-orange-500 focus:ring-orange-400"
      />
      {label}
    </label>
  );
}

function TableHead({ cols }) {
  return (
    <thead>
      <tr className="border-b border-slate-100">
        {cols.map(col => (
          <th key={col} className="py-2.5 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{col}</th>
        ))}
      </tr>
    </thead>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  // DATE columns arrive as "YYYY-MM-DD" or UTC-midnight ISO strings; both
  // render a day early in US timezones. Pin them to local midnight.
  const v = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/.test(d)
    ? `${d.slice(0, 10)}T00:00:00` : d;
  return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Provision boolean keys on contracts (14 total) — must match fdr_contracts columns
// and ContractManager's PROVISION_LABELS exactly
const PROVISION_KEYS = [
  'has_compliance_program_req', 'has_code_of_conduct_req', 'has_compliance_officer_req',
  'has_training_req', 'has_monitoring_req', 'has_reporting_req', 'has_enforcement_req',
  'has_downstream_oversight_req', 'has_exclusion_screening_req', 'has_records_access_req',
  'has_data_privacy_req', 'has_hipaa_baa', 'has_performance_standards', 'has_insurance_req',
];

// ─── DETAIL TABS ─────────────────────────────────────────────────────────────

const DETAIL_TABS = [
  { id: 'overview',     label: 'Overview' },
  { id: 'contacts',     label: 'Contacts' },
  { id: 'contracts',    label: 'Contracts' },
  { id: 'assessments',  label: 'Assessments' },
  { id: 'findings',     label: 'Findings' },
  { id: 'attestations', label: 'Attestations' },
  { id: 'screening',    label: 'Screening' },
  { id: 'documents',    label: 'Documents' },
  { id: 'security',     label: 'Security & HIPAA' },
  { id: 'ai',           label: 'AI Systems' },
  { id: 'activity',     label: 'Activity Log' },
];

// ─── TAB CONTENTS ────────────────────────────────────────────────────────────

function OverviewTab({ entity, contacts, contracts, findings, refresh }) {
  const primaryContact = contacts?.find(c => c.is_primary);
  const complianceContact = contacts?.find(c => c.is_compliance_contact);
  const openFindings = findings?.filter(f => f.status === 'open' || f.status === 'in_progress') ?? [];
  const openCAPs = findings?.reduce((n, f) => n + (f.cap_count ?? 0), 0) ?? 0;

  const riskFactors = (() => {
    if (!entity.risk_factors) return null;
    if (typeof entity.risk_factors === 'string') {
      try { return JSON.parse(entity.risk_factors); } catch { return null; }
    }
    return entity.risk_factors;
  })();

  return (
    <div className="space-y-5">
      {/* Objective scorecard — computed from the record, evidence per component */}
      <ScorecardCard entityId={entity.id} />

      {/* Info cards row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <CardSection title="Address">
          <div className="space-y-1 text-sm text-slate-600">
            {entity.address_line1 && <div>{entity.address_line1}</div>}
            {entity.address_line2 && <div>{entity.address_line2}</div>}
            {(entity.city || entity.state || entity.zip) && (
              <div>{[entity.city, entity.state, entity.zip].filter(Boolean).join(', ')}</div>
            )}
            {entity.country && entity.country !== 'US' && <div>{entity.country}</div>}
            {!entity.address_line1 && !entity.city && <span className="text-slate-300 text-xs">No address on file</span>}
          </div>
        </CardSection>

        <CardSection title="Primary Contact">
          {primaryContact ? (
            <div className="space-y-0.5 text-sm text-slate-600">
              <div className="font-medium text-slate-800">{primaryContact.first_name} {primaryContact.last_name}</div>
              {primaryContact.title && <div className="text-xs text-slate-400">{primaryContact.title}</div>}
              {primaryContact.email && <div className="text-xs text-slate-500">{primaryContact.email}</div>}
              {primaryContact.phone && <div className="text-xs text-slate-500">{primaryContact.phone}</div>}
            </div>
          ) : (
            <span className="text-slate-300 text-xs">No primary contact</span>
          )}
        </CardSection>

        <CardSection title="Compliance Officer">
          {complianceContact ? (
            <div className="space-y-0.5 text-sm text-slate-600">
              <div className="font-medium text-slate-800">{complianceContact.first_name} {complianceContact.last_name}</div>
              {complianceContact.title && <div className="text-xs text-slate-400">{complianceContact.title}</div>}
              {complianceContact.email && <div className="text-xs text-slate-500">{complianceContact.email}</div>}
            </div>
          ) : (
            <span className="text-slate-300 text-xs">No compliance contact</span>
          )}
        </CardSection>

        <CardSection title="Key Dates">
          <div className="space-y-2">
            <InfoRow label="Initial Due Diligence" value={fmtDate(entity.initial_due_diligence_date)} />
            <InfoRow label="Last Assessment" value={fmtDate(entity.last_assessment_date)} />
            <InfoRow label="Next Assessment Due" value={fmtDate(entity.next_assessment_due_date)} />
            <InfoRow label="Last Exclusion Screening" value={fmtDate(entity.last_exclusion_screening_date)} />
          </div>
        </CardSection>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Contracts" value={contracts?.length} />
        <StatTile label="Delegated Functions" value={entity.delegated_functions_count} />
        <StatTile label="Open Findings" value={openFindings.length} accent={openFindings.length > 0} />
        <StatTile label="Open CAPs" value={openCAPs} accent={openCAPs > 0} />
      </div>

      {/* Risk Factors */}
      {riskFactors && (
        <CardSection title="Risk Factors">
          {Array.isArray(riskFactors) ? (
            <div className="flex flex-wrap gap-2">
              {riskFactors.map((f, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">{f}</span>
              ))}
            </div>
          ) : typeof riskFactors === 'object' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(riskFactors).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="font-medium text-slate-700">{String(v)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-600">{String(riskFactors)}</p>
          )}
        </CardSection>
      )}

      {/* Accreditations — the matrix's Accreditation column lives here */}
      {entity.accreditations && (
        <CardSection title="Accreditations">
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{entity.accreditations}</p>
        </CardSection>
      )}

      {/* Notes */}
      {entity.notes && (
        <CardSection title="Notes">
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{entity.notes}</p>
        </CardSection>
      )}
    </div>
  );
}

function ContactsTab({ entityId, contacts, refresh }) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', title: '', email: '', phone: '', npi: '',
    is_primary: false, is_compliance_contact: false, is_owner_officer: false,
  });

  const set = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await fetch(`${API}/api/fdr/entities/${entityId}/contacts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setForm({ first_name: '', last_name: '', title: '', email: '', phone: '', npi: '', is_primary: false, is_compliance_contact: false, is_owner_officer: false });
      setShowForm(false);
      refresh();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setShowForm(s => !s)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 transition-opacity shadow-sm"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Contact
        </button>
      </div>

      {showForm && (
        <Card className="p-5">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">New Contact</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LabeledInput label="First Name" value={form.first_name} onChange={set('first_name')} required />
              <LabeledInput label="Last Name" value={form.last_name} onChange={set('last_name')} required />
              <LabeledInput label="Title" value={form.title} onChange={set('title')} />
              <LabeledInput label="Email" type="email" value={form.email} onChange={set('email')} />
              <LabeledInput label="Phone" type="tel" value={form.phone} onChange={set('phone')} />
              <LabeledInput label="NPI" value={form.npi} onChange={set('npi')} />
            </div>
            <div className="flex flex-wrap gap-4 pt-1">
              <LabeledCheckbox label="Primary Contact" checked={form.is_primary} onChange={set('is_primary')} />
              <LabeledCheckbox label="Compliance Contact" checked={form.is_compliance_contact} onChange={set('is_compliance_contact')} />
              <LabeledCheckbox label="Owner / Officer" checked={form.is_owner_officer} onChange={set('is_owner_officer')} />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60">
                {saving ? 'Saving...' : 'Save Contact'}
              </button>
            </div>
          </form>
        </Card>
      )}

      {contacts.length === 0 ? (
        <Card><EmptyState message="No contacts on file" /></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <TableHead cols={['Name', 'Title', 'Email', 'Phone', 'NPI', 'Roles']} />
              <tbody className="divide-y divide-slate-50">
                {contacts.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4 font-medium text-slate-800">{c.first_name} {c.last_name}</td>
                    <td className="py-3 px-4 text-slate-500">{c.title || '—'}</td>
                    <td className="py-3 px-4 text-slate-500">{c.email || '—'}</td>
                    <td className="py-3 px-4 text-slate-500">{c.phone || '—'}</td>
                    <td className="py-3 px-4 text-slate-500 font-mono text-xs">{c.npi || '—'}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {c.is_primary && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">Primary</span>}
                        {c.is_compliance_contact && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">Compliance</span>}
                        {c.is_contract_signer && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">Signer</span>}
                        {c.is_owner_officer && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">Owner</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ContractsTab({ contracts, refresh }) {
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [functionFor, setFunctionFor] = useState(null);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDetail(data);
    } catch {}
  }, []);
  useEffect(() => { setDetail(null); if (openId) loadDetail(openId); }, [openId, loadDetail]);

  if (contracts.length === 0) return <Card><EmptyState message="No contracts on file" /></Card>;

  // Opening a contract here renders the EXACT same workspace as the platform
  // Contracts tab — one shared ContractWorkspace component serves both, so
  // provisions, delegated functions, uploads, the scope banner, the run
  // button, and the findings are identical no matter which door was used.
  if (openId) {
    const c = contracts.find(x => x.id === openId);
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => { setOpenId(null); refresh?.(); }}
            className="text-xs font-semibold text-slate-500 hover:text-slate-800">← All contracts</button>
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-sm font-semibold text-slate-800 truncate">{c?.title || 'Contract'}</span>
            {c && <StatusPill contract={c} onChanged={refresh} />}
          </div>
        </div>
        {detail ? (
          <Card className="p-5">
            <ContractWorkspace detail={detail} onAddFunction={() => setFunctionFor(detail.contract)} />
          </Card>
        ) : (
          <Card className="p-5 text-sm text-slate-400">Loading contract…</Card>
        )}
        {functionFor && (
          <AddFunctionModal contract={functionFor}
            onClose={() => setFunctionFor(null)}
            onCreated={() => { setFunctionFor(null); loadDetail(openId); refresh?.(); }} />
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {contracts.map(c => <EntityContractCard key={c.id} contract={c} refresh={refresh} onOpen={() => setOpenId(c.id)} />)}
    </div>
  );
}

/**
 * One contract on the vendor page, telling the whole story where the reader
 * is: live changeable status (same pill as the register), the documents on
 * file, and the agent's executive briefing with its scope — never a grade.
 */
function EntityContractCard({ contract: c, refresh, onOpen }) {
  const [review, setReview] = useState(null);
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    let on = true;
    fetch(`${API}/api/fdr/contracts/${c.id}/review`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (on && d?.success) { setReview(d.review || null); setDocs(d.documents || []); } })
      .catch(() => {});
    return () => { on = false; };
  }, [c.id]);
  const provisionCount = PROVISION_KEYS.filter(k => c[k]).length;

  return (
    <Card className="p-5 flex flex-col gap-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {c.contract_number && (
              <span className="text-xs font-mono text-slate-400">{c.contract_number}</span>
            )}
            <StatusPill contract={c} onChanged={refresh} />
          </div>
          <h4 className="text-sm font-semibold text-slate-800 leading-snug">{c.title || c.contract_title || 'Untitled Contract'}</h4>
        </div>
        <button onClick={onOpen}
          className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 shadow-sm whitespace-nowrap">
          Open contract →
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <InfoRow label="Effective" value={fmtDate(c.effective_date)} />
        <InfoRow label="Expires" value={fmtDate(c.expiration_date)} />
        <InfoRow label="Functions" value={c.function_count ?? 'none recorded'} />
      </div>

      {docs.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {docs.map(d => {
            const isPdf = /pdf/i.test(d.mime_type || d.file_name || '');
            const url = `${API}/api/fdr/contracts/${c.id}/documents/${d.id}/file${isPdf ? '?inline=1' : ''}`;
            return (
              <a key={d.id} href={url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-[11.5px] font-medium text-slate-600 hover:bg-slate-50">
                <span aria-hidden>📄</span>
                <span className="max-w-[180px] truncate">{d.file_name}</span>
                {d.doc_role === 'baa' && <span className="text-[9.5px] font-bold uppercase text-emerald-600">BAA</span>}
                {d.doc_role === 'amendment' && <span className="text-[9.5px] font-bold uppercase text-sky-600">amendment</span>}
              </a>
            );
          })}
        </div>
      )}

      {/* No summary here by operator rule: the card is a doorway. The
          briefing, suggestions, and scope live in the workspace behind
          Open contract, identical to the platform Contracts tab. */}
      {!docs.length && (
        <p className="text-[11px] text-slate-400 border-t border-slate-100 pt-3">No documents on file yet. Open the contract to upload them.</p>
      )}
    </Card>
  );
}

function AssessmentsTab({ entityId, assessments, refresh }) {
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 transition-opacity shadow-sm"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Assessment
        </button>
      </div>

      {assessments.length === 0 ? (
        <Card><EmptyState message="No assessments recorded" /></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <TableHead cols={['Type', 'Methodology', 'Status', 'Risk Score', 'Scheduled', 'Completed', 'Assessor']} />
              <tbody className="divide-y divide-slate-50">
                {assessments.map(a => (
                  <tr key={a.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4 font-medium text-slate-800">{a.assessment_type}</td>
                    <td className="py-3 px-4 text-slate-500">{a.methodology || '—'}</td>
                    <td className="py-3 px-4">
                      <Badge cfg={ASSESSMENT_STATUS_CFG} value={a.status} />
                    </td>
                    <td className="py-3 px-4">
                      {a.risk_score != null ? (
                        <span className="font-semibold text-slate-700">{a.risk_score}</span>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-4 text-slate-500">{fmtDate(a.scheduled_date)}</td>
                    <td className="py-3 px-4 text-slate-500">{fmtDate(a.completed_at)}</td>
                    <td className="py-3 px-4 text-slate-500">{a.assessor_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-slate-800 mb-1">New Assessment</h3>
            <p className="text-sm text-slate-400 mb-5">Assessment scheduling is managed from the Assessment Workflow module. Use that module to schedule a new assessment for this entity.</p>
            <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Close</button>
          </Card>
        </div>
      )}
    </div>
  );
}

function FindingsTab({ findings }) {
  const [expanded, setExpanded] = useState(null);

  if (findings.length === 0) return <Card><EmptyState message="No findings recorded" /></Card>;

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <TableHead cols={['Title', 'Severity', 'Category', 'Status', 'Due Date', 'CAPs']} />
          <tbody className="divide-y divide-slate-50">
            {findings.map(f => (
              <React.Fragment key={f.id}>
                <tr
                  className="hover:bg-slate-50/60 transition-colors cursor-pointer"
                  onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <svg
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded === f.id ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="font-medium text-slate-800">{f.title}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge cfg={SEVERITY_CFG} value={f.severity} />
                  </td>
                  <td className="py-3 px-4 text-slate-500">{f.category || '—'}</td>
                  <td className="py-3 px-4">
                    <Badge cfg={FINDING_STATUS_CFG} value={f.status} />
                  </td>
                  <td className="py-3 px-4 text-slate-500">{fmtDate(f.due_date)}</td>
                  <td className="py-3 px-4">
                    {f.cap_count > 0 ? (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-200">{f.cap_count}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
                {expanded === f.id && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50/80 px-8 py-4 border-b border-slate-100">
                      <div className="space-y-2">
                        {f.description && <p className="text-sm text-slate-600">{f.description}</p>}
                        {f.caps && f.caps.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Corrective Action Plans</p>
                            <div className="space-y-1.5">
                              {f.caps.map(cap => (
                                <div key={cap.id} className="flex items-center gap-3 text-sm">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cap.status === 'completed' ? 'bg-emerald-400' : cap.status === 'in_progress' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                                  <span className="text-slate-700 font-medium">{cap.title}</span>
                                  <span className="text-slate-400 text-xs">Due {fmtDate(cap.due_date)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(!f.caps || f.caps.length === 0) && !f.description && (
                          <p className="text-xs text-slate-400">No additional details</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const ATTESTATION_ELEMENTS = [
  'compliance_program', 'policies_procedures', 'training_completion', 'audit_rights_acknowledgment',
  'hipaa_compliance', 'fraud_waste_abuse', 'exclusion_screening', 'incident_reporting',
];

function AttestationsTab({ attestations }) {
  if (attestations.length === 0) return <Card><EmptyState message="No attestations on file" /></Card>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {attestations.map(a => {
        const statusEntry = ATTESTATION_STATUS_CFG[a.status?.toLowerCase()] ?? null;

        return (
          <Card key={a.id} className="p-5 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-lg font-bold text-slate-800">{a.attestation_year}</span>
                {a.attestation_type && <p className="text-xs text-slate-400 mt-0.5">{a.attestation_type}</p>}
              </div>
              {statusEntry && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusEntry.color}`}>{statusEntry.label}</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InfoRow label="Sent" value={fmtDate(a.sent_at)} />
              <InfoRow label="Received" value={fmtDate(a.received_at)} />
            </div>

            {/* Element checkboxes */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Elements</p>
              <div className="grid grid-cols-2 gap-1.5">
                {ATTESTATION_ELEMENTS.map(el => {
                  const checked = !!a[el];
                  return (
                    <div key={el} className="flex items-center gap-1.5">
                      <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${checked ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-200'}`}>
                        {checked && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-[11px] ${checked ? 'text-slate-600' : 'text-slate-400'} capitalize leading-tight`}>
                        {el.replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function ScreeningTab({ results }) {
  if (results.length === 0) return <Card><EmptyState message="No screening results on file" /></Card>;

  return (
    <Card>
      <ul className="divide-y divide-slate-100">
        {results.map((r, i) => (
          <li key={r.id ?? i} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50/60 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-800 text-sm">{r.matched_name || r.screened_name || '—'}</span>
                {r.match_source && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">{r.match_source}</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{fmtDate(r.screening_date)}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge cfg={CONFIDENCE_CFG} value={r.match_confidence} fallback="—" />
              <Badge cfg={RESOLUTION_CFG} value={r.resolution} fallback="Pending" />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const ACTIVITY_ICONS = {
  assessment:   'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  contact:      'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  document:     'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  screening:    'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  finding:      'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  default:      'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

const OUTCOME_CFG = {
  pass:      { label: 'Pass',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  fail:      { label: 'Fail',     color: 'bg-red-50 text-red-700 border-red-200' },
  completed: { label: 'Completed',color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending:   { label: 'Pending',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  flagged:   { label: 'Flagged',  color: 'bg-orange-50 text-orange-700 border-orange-200' },
};

// CMS oversight activity types (migration 072) — each logged row is born a
// valid FTEAM work-plan line for the program-audit universe.
const ACTIVITY_TYPES = [
  'site_visit', 'desk_audit', 'virtual_audit', 'training_verification', 'report_review',
  'performance_review', 'policy_distribution', 'communication', 'corrective_action_followup',
  'exclusion_screening', 'attestation_collection', 'contract_review', 'complaint_investigation',
];

/** Log Oversight Activity — wires the previously-orphaned POST /api/fdr/activities. */
function LogActivityModal({ entityId, onClose, onCreated }) {
  const [form, setForm] = useState({
    activity_type: 'report_review', title: '', completed_date: new Date().toISOString().split('T')[0],
    duration_hours: '', outcome: '', summary: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const cls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const body = { entity_id: entityId, ...form };
      if (!body.duration_hours) delete body.duration_hours;
      if (!body.outcome) delete body.outcome;
      if (!body.summary) delete body.summary;
      const res = await fetch(`${API}/api/fdr/activities`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) onCreated(); else setErr(data.message || 'Failed');
    } catch { setErr('Failed to log activity'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">Log Oversight Activity</h3>
          <p className="text-xs text-slate-400 mt-0.5">Unrecorded oversight didn't happen, as far as an auditor is concerned.</p>
        </div>
        <form onSubmit={submit} className="px-8 py-5 space-y-4">
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Activity type *</label>
              <select value={form.activity_type} onChange={e => setForm({ ...form, activity_type: e.target.value })} className={cls}>
                {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Completed date</label>
              <input type="date" value={form.completed_date} onChange={e => setForm({ ...form, completed_date: e.target.value })} className={cls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
              placeholder="e.g., Q3 claims TAT report review" className={cls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Outcome</label>
              <select value={form.outcome} onChange={e => setForm({ ...form, outcome: e.target.value })} className={cls}>
                <option value="">—</option>
                <option value="satisfactory">Satisfactory</option>
                <option value="needs_improvement">Needs improvement</option>
                <option value="unsatisfactory">Unsatisfactory</option>
                <option value="informational">Informational</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hours spent</label>
              <input type="number" step="0.5" min="0" value={form.duration_hours}
                onChange={e => setForm({ ...form, duration_hours: e.target.value })} className={cls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Summary</label>
            <textarea value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} rows={3}
              placeholder="What was reviewed, what it showed, any deficiencies" className={cls} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50">
              {saving ? 'Logging…' : 'Log Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ActivityTab({ activities, entityId, refresh }) {
  const [showLog, setShowLog] = useState(false);

  const logButton = (
    <div className="flex justify-end mb-3">
      <button onClick={() => setShowLog(true)}
        className="px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-sm hover:shadow transition-all">
        Log Activity
      </button>
      {showLog && <LogActivityModal entityId={entityId} onClose={() => setShowLog(false)}
        onCreated={() => { setShowLog(false); refresh?.(); }} />}
    </div>
  );

  if (activities.length === 0) return (
    <div>
      {logButton}
      <Card><EmptyState message="No activity recorded — site visits, report reviews, and screenings logged here become the FTEAM audit universe" /></Card>
    </div>
  );

  return (
    <div>
    {logButton}
    <Card className="p-5">
      <ul className="relative space-y-0">
        {/* vertical line */}
        <li className="absolute left-4 top-0 bottom-0 w-px bg-slate-100 pointer-events-none" aria-hidden />

        {activities.map((a, i) => {
          const iconPath = ACTIVITY_ICONS[a.activity_type?.toLowerCase()] ?? ACTIVITY_ICONS.default;
          return (
            <li key={a.id ?? i} className="relative flex gap-4 pb-5 last:pb-0">
              <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
                </svg>
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{a.title || a.activity_type}</p>
                    {a.conducted_by_name && <p className="text-xs text-slate-400 mt-0.5">by {a.conducted_by_name}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.outcome && <Badge cfg={OUTCOME_CFG} value={a.outcome} />}
                    <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(a.completed_date)}</span>
                  </div>
                </div>
                {a.notes && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{a.notes}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
    </div>
  );
}

// ─── EDIT PANEL ───────────────────────────────────────────────────────────────

const EDITABLE_FIELDS = [
  // Every field the onboarding form captures, under the names the vendor
  // record actually uses (operator order 2026-09-21: most showed blank).
  { key: 'name', label: 'Vendor Name', type: 'text' },
  { key: 'legal_name', label: 'Legal Name', type: 'text' },
  { key: 'entity_type', label: 'Entity Type', type: 'select', options: ['first_tier', 'downstream', 'related'] },
  { key: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'suspended', 'terminated', 'pending'] },
  { key: 'termination_date', label: 'Termination date', type: 'date', hint: 'The vendor and its open contracts end on this date. Until then oversight continues as it is. Leave blank if the vendor is not terminating.' },
  { key: 'risk_level', label: 'Risk Level', type: 'select', options: ['low', 'medium', 'high', 'critical'] },
  { key: 'tier', label: 'Tier', type: 'select', options: ['1', '2', '3', '4', '5'] },
  { key: 'tax_id', label: 'Tax ID (EIN)', type: 'text' },
  { key: 'npi', label: 'NPI', type: 'text' },
  { key: 'address_line1', label: 'Address Line 1', type: 'text' },
  { key: 'address_line2', label: 'Address Line 2', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State', type: 'text' },
  { key: 'zip', label: 'ZIP', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'website', label: 'Website', type: 'text' },
  { key: 'primary_contact_name', label: 'Primary Contact Name', type: 'text' },
  { key: 'primary_contact_email', label: 'Primary Contact Email', type: 'text' },
  { key: 'primary_contact_phone', label: 'Primary Contact Phone', type: 'text' },
  { key: 'compliance_officer_name', label: 'Compliance Officer Name', type: 'text' },
  { key: 'compliance_officer_email', label: 'Compliance Officer Email', type: 'text' },
  { key: 'accreditations', label: 'Accreditations (e.g. NCQA Cred & UM, URAC-Pharmacy)', type: 'text' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
];

function EditPanel({ entity, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const out = {};
    EDITABLE_FIELDS.forEach(f => { const v = f.key === 'name' ? (entity.name ?? entity.entity_name) : entity[f.key]; out[f.key] = f.type === 'date' ? String(v || '').slice(0, 10) : (v == null ? '' : String(v)); });
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    // Send only what changed. A field left blank that was blank stays
    // untouched (never '' over NULL); tier is a number on the record.
    const payload = Object.fromEntries(Object.entries(form).flatMap(([k, v]) => {
          const was = k === 'name' ? (entity.name ?? entity.entity_name) : entity[k];
          const before = was == null ? '' : (EDITABLE_FIELDS.find(f => f.key === k)?.type === 'date' ? String(was).slice(0, 10) : String(was));
          if (v === before) return [];
          if (k === 'tier') return v === '' ? [] : [[k, Number(v)]];
          if (k === 'name' && !String(v).trim()) return [];
          return [[k, v]];
        }));
    if (!Object.keys(payload).length) { setSaving(false); onClose(); return; }
    try {
      const res = await fetch(`${API}/api/fdr/entities/${entity.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-end z-50">
      <div className="h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-800">Edit Entity</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {EDITABLE_FIELDS.map(f => {
            if (f.type === 'select') {
              return (
                <div key={f.key} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{f.label}</label>
                  <select
                    value={form[f.key] ?? ''}
                    onChange={e => set(f.key)(e.target.value)}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  >
                    <option value="">— Select —</option>
                    {f.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              );
            }
            if (f.type === 'textarea') {
              return (
                <div key={f.key} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{f.label}</label>
                  <textarea
                    rows={4}
                    value={form[f.key] ?? ''}
                    onChange={e => set(f.key)(e.target.value)}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
                  />
                </div>
              );
            }
            if (f.hint) {
              return (
                <div key={f.key} className="flex flex-col gap-1">
                  <LabeledInput label={f.label} type={f.type} value={form[f.key]} onChange={set(f.key)} />
                  <p className="text-[11.5px] text-slate-400 leading-snug">{f.hint}</p>
                </div>
              );
            }
            return <LabeledInput key={f.key} label={f.label} type={f.type} value={form[f.key]} onChange={set(f.key)} />;
          })}
          {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        </form>

        <div className="px-6 py-4 border-t border-slate-100 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function EntityDetail({ entityId, onBack, onOpenEntity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/fdr/entities/${entityId}`, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load entity');
      setData(json);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;

  if (error || !data?.entity) {
    return (
      <div className="p-8">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 font-medium mb-6 transition-colors flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Registry
        </button>
        <Card className="p-8 text-center">
          <p className="text-sm text-red-500 font-medium">{error ?? 'Entity not found'}</p>
        </Card>
      </div>
    );
  }

  const {
    entity,
    contacts = [],
    contracts = [],
    assessments = [],
    findings = [],
    attestations = [],
    recent_activities = [],
    recent_screening_results = [],
  } = data;

  const typeEntry = ENTITY_TYPE_CFG[entity.entity_type?.toLowerCase()] ?? null;
  const statusEntry = STATUS_CFG[entity.status?.toLowerCase()] ?? null;
  const riskEntry = RISK_LEVEL_CFG[entity.risk_level?.toLowerCase()] ?? null;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-700 font-medium mb-4 transition-colors flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Registry
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {typeEntry && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${typeEntry.color}`}>{typeEntry.label}</span>
              )}
              {statusEntry && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusEntry.color}`}>{statusEntry.label}</span>
              )}
              {entity.termination_date && (() => {
                const d = String(entity.termination_date).slice(0, 10);
                const [y, m, dd] = d.split('-').map(Number);
                const label = new Date(y, m - 1, dd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const done = entity.status === 'terminated';
                return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${done ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{done ? `Terminated ${label}` : `Terminating ${label}`}</span>;
              })()}
              {riskEntry && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${riskEntry.color}`}>
                  {riskEntry.label} Risk
                  {entity.risk_score != null && <span className="ml-1 opacity-70">· {entity.risk_score}</span>}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-slate-800 tracking-tight leading-tight">{entity.entity_name || entity.name}</h2>
            {entity.tax_id && <p className="text-xs text-slate-400 mt-1 font-mono">EIN {entity.tax_id}</p>}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowCopilot(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
              Copilot
            </button>
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 transition-opacity shadow-sm shadow-orange-500/20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-slate-200">
        <div className="flex gap-0 overflow-x-auto scrollbar-hide">
          {DETAIL_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-orange-500 text-orange-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div>
        {tab === 'overview' && (
          <OverviewTab
            entity={entity}
            contacts={contacts}
            contracts={contracts}
            findings={findings}
            refresh={load}
          />
        )}
        {tab === 'contacts' && (
          <ContactsTab entityId={entityId} contacts={contacts} refresh={load} />
        )}
        {tab === 'contracts' && (
          <ContractsTab contracts={contracts} refresh={load} />
        )}
        {tab === 'assessments' && (
          <AssessmentsTab entityId={entityId} assessments={assessments} refresh={load} />
        )}
        {tab === 'findings' && (
          <FindingsTab findings={findings} />
        )}
        {tab === 'attestations' && (
          <AttestationsTab attestations={attestations} />
        )}
        {tab === 'screening' && (
          <ScreeningTab results={recent_screening_results} />
        )}
        {tab === 'documents' && (
          <Attachments entityId={entityId} recordKind="entity" title="Entity documents" />
        )}
        {tab === 'security' && (
          <SecurityTab entityId={entityId} />
        )}
        {tab === 'ai' && (
          <AiSystemsTab entityId={entityId} />
        )}
        {tab === 'activity' && (
          <ActivityTab activities={recent_activities} entityId={entityId} refresh={load} />
        )}
      </div>

      {showCopilot && entity && (
        <CopilotPanel entityId={entityId} entityName={entity.name} onClose={() => setShowCopilot(false)} />
      )}

      {/* Edit slide-over */}
      {editing && (
        <EditPanel
          entity={entity}
          onClose={() => setEditing(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}

/** ScorecardCard — the objective vendor grade. Every component names its
 *  evidence; nothing is self-reported. GET /api/fdr/entities/:id/scorecard */
function ScorecardCard({ entityId }) {
  const [sc, setSc] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/fdr/entities/${entityId}/scorecard`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (d.success) setSc(d.scorecard); }).catch(() => {});
  }, [entityId]);

  if (!sc) return null;
  const gradeColor = sc.grade === 'A' ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
    : sc.grade === 'B' ? 'text-lime-600 bg-lime-50 border-lime-200'
    : sc.grade === 'C' ? 'text-amber-600 bg-amber-50 border-amber-200'
    : sc.grade === 'D' ? 'text-orange-600 bg-orange-50 border-orange-200'
    : 'text-red-600 bg-red-50 border-red-200';
  const COMPONENT_LABELS = {
    assessment: 'Assessment', findings: 'Findings & CAPs', screening: 'Screening',
    attestation: 'Attestation', monitoring: 'Monitoring cadence',
  };
  const barColor = (v) => v >= 85 ? 'bg-emerald-500' : v >= 70 ? 'bg-amber-500' : v >= 50 ? 'bg-orange-500' : 'bg-red-500';

  return (
    <Card className="p-5">
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 rounded-2xl border-2 flex items-center justify-center flex-shrink-0 ${gradeColor}`}>
          <span className="text-3xl font-black">{sc.grade}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-bold text-slate-800">Oversight Scorecard</span>
            <span className="text-2xl font-bold text-slate-800">{sc.composite}</span>
            <span className="text-xs text-slate-400">/100 · computed from the record, not self-reported</span>
          </div>
          <div className="flex gap-2 mt-2">
            {Object.entries(sc.components).map(([k, c]) => (
              <div key={k} className="flex-1 min-w-0" title={`${COMPONENT_LABELS[k]}: ${c.score}/100 — ${c.evidence}`}>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${barColor(c.score)}`} style={{ width: `${c.score}%` }} />
                </div>
                <div className="text-[9px] text-slate-400 truncate mt-0.5">{COMPONENT_LABELS[k]} · {Math.round(c.score)}</div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)} className="text-xs font-bold text-orange-600 hover:text-orange-700 flex-shrink-0">
          {open ? 'Hide evidence' : 'Evidence'}
        </button>
      </div>
      {open && (
        <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-1 gap-1.5">
          {Object.entries(sc.components).map(([k, c]) => (
            <div key={k} className="flex items-baseline gap-2 text-xs">
              <span className="font-bold text-slate-600 w-32 flex-shrink-0">{COMPONENT_LABELS[k]} ({sc.weights[k]}%)</span>
              <span className={`font-bold w-8 text-right ${c.score >= 85 ? 'text-emerald-600' : c.score >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{Math.round(c.score)}</span>
              <span className="text-slate-500">{c.evidence}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
